import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AlarmClock, Pause, Timer } from "lucide-react";
import { Button, cx } from "./ui";
import { useToast } from "./Toast";
import { api } from "@/lib/api";
import { useAction, useBanks } from "@/lib/queries";
import { formatDuration } from "@shared/duration";
import { dueWarning, type WarningMinutes } from "@shared/screen-time";
import type { TimeSessionDTO } from "@shared/types";

const MINUTE = 60_000;

/**
 * Milliseconds left on a countdown, ticking every second. The server sends what's left as of its
 * response; counting from when the data arrived (`receivedAt`) keeps a wrong device clock out of it.
 */
export function useSessionClock(session: TimeSessionDTO | null | undefined, receivedAt: number): number | null {
  const endsAt = session ? receivedAt + session.remainingMs : null;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (endsAt === null) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return endsAt === null ? null : Math.max(0, endsAt - now);
}

/** Whole minutes left, rounded down ("12 min", "1 hr 5 min"); under a minute reads "under 1 min". */
export function formatRemaining(ms: number): string {
  const minutes = Math.floor(ms / MINUTE);
  return minutes < 1 ? "under 1 min" : formatDuration(minutes);
}

export function StopTimeButton({ session, kidId, label = "Stop & save the rest" }: { session: TimeSessionDTO; kidId?: string; label?: string }) {
  const stop = useAction(
    () => api.post<{ savedMinutes: number }>(`/rewards/${session.rewardTypeId}/stop`, kidId ? { kidId } : {}),
    { success: (r) => (r.savedMinutes > 0 ? `${formatDuration(r.savedMinutes)} saved for later` : "Screen time stopped") },
  );
  return (
    <Button size="sm" variant="secondary" onClick={() => stop.mutate()} loading={stop.isPending}>
      <Pause className="size-4" /> {label}
    </Button>
  );
}

/**
 * Kid-wide countdown: a bar on every kid page (the Shop card shows its own), the 5- and 1-minute
 * warnings as toasts while the app is open, and "time's up" when it reaches zero. Push
 * notifications for the same moments come from the server, for when the app is closed.
 */
export function KidScreenTimeBar() {
  const banks = useBanks();
  const toast = useToast();
  const qc = useQueryClient();
  const onShop = useLocation().pathname.startsWith("/kid/shop");
  const session = banks.data?.banks.find((b) => b.session)?.session ?? null;
  const left = useSessionClock(session, banks.dataUpdatedAt);
  const warned = useRef(new Map<string, WarningMinutes[]>());
  const [timeUp, setTimeUp] = useState<string | null>(null);

  useEffect(() => {
    if (!session || left === null) return;
    if (left <= 0) {
      if (timeUp !== session.id) {
        setTimeUp(session.id);
        void qc.invalidateQueries();
      }
      return;
    }
    const sent = warned.current.get(session.id) ?? [];
    const due = dueWarning({ startedAt: session.startedAt, endsAt: Date.now() + left, minutes: session.minutes }, Date.now(), sent);
    if (due !== null) {
      warned.current.set(session.id, [...sent, due]);
      toast.show(due === 1 ? "1 minute left — time to wrap up!" : `${due} minutes left — start wrapping up`, "info");
    }
  }, [session, left, timeUp, toast, qc]);

  if (timeUp && (!session || session.id === timeUp)) {
    return (
      <div role="alert" className="sticky top-0 z-20 -mx-4 mb-2 flex items-center gap-3 bg-danger px-4 py-3 text-white md:mx-0 md:mt-3 md:rounded-2xl">
        <AlarmClock className="size-6 shrink-0" />
        <span className="flex-1 font-extrabold">Time's up! Screen time is over.</span>
        <Button size="sm" variant="secondary" onClick={() => setTimeUp(null)}>
          OK
        </Button>
      </div>
    );
  }
  if (!session || left === null || left <= 0 || onShop) return null;
  const soon = left <= 5 * MINUTE;
  return (
    <div
      role="status"
      className={cx(
        "sticky top-0 z-20 -mx-4 mb-2 flex items-center gap-3 px-4 py-2.5 md:mx-0 md:mt-3 md:rounded-2xl",
        soon ? "bg-warn text-white" : "bg-coin-soft text-coin-ink",
      )}
    >
      <Timer className="size-5 shrink-0" />
      <span className="flex-1 font-extrabold">
        Screen time · <span className="tabular-nums">{formatRemaining(left)}</span> left
      </span>
      <StopTimeButton session={session} label="Stop" />
    </div>
  );
}
